import { Button, Group, Modal, ScrollArea, Stack } from '@mantine/core';

import { useParams } from 'react-router-dom';

import { useCallback, useState } from 'react';

import { useDisclosure } from '@mantine/hooks';

import ResourceListItem from './ResourceListItem';
import useResourceListData, { RESOURCE_LABEL_TO_DIRECTION } from '../../hooks/useResourceListData';
import ResourceFilterGroup from '../ResourceFilterGroup';
import ResourceSortingSelector from './ResourceSortingSelector';
import CenteredLoader from '../CenteredLoader';
import TableEmptyState, { type TableEmptyStateProps } from '../Table/TableEmptyState';

import { type ResourceFilterState, type ResourceType } from '../../types/miscTypes';
import {
  ADMIN_EMPTY_RESOURCES_STATE_TEXT,
  DEFAULT_RESOURCE_FILTER_STATE,
  EMPTY_RESOURCES_FILTER_STATE_TEXT,
  NON_ADMIN_EMPTY_RESOURCES_STATE_TEXT
} from '../../utils/constants';
import { type OrganizationResource } from '../../types/apiTypes';

import { defaultModalProps } from '../../utils/mantineUtils';
import { EditResource, UploadResources } from '../Forms';
import DeleteResource from '../Modals/DeleteResource/DeleteResource';
import { ManageOrganizationResourceTags } from '../Modals';
import useEnsisQuery from '../../hooks/useEnsisQuery';
import useOrgPermissions from '../../hooks/useOrgPermissions';
import { FileUploadIcon } from '../../icons';
import Pagination from '../Pagination';

interface Props {
  selectedResourceUids?: string[]
  onSelectResource?: (resourceUid: string) => void
  allowResourceUpload: boolean
  title: React.ReactNode
  defaultFilter?: ResourceFilterState
}

const ResourceList: React.FC<Props> = (props: Props) => {
  const { organization, proposalUid } = useParams();
  const {
    allowResourceUpload,
    selectedResourceUids,
    onSelectResource,
    title,
    defaultFilter
  } = props;

  const [filters, setFilters] = useState<ResourceFilterState>(
    defaultFilter ?? DEFAULT_RESOURCE_FILTER_STATE
  );
  const handleUpdateFilters = useCallback((newFilters: ResourceFilterState) => {
    setFilters(newFilters);
    if (newFilters.searchTerm !== '') {
      onSortingChange([{ id: '', desc: true }]);
    }
  }, []);

  const { data, isLoading: tagsLoading } = useEnsisQuery(
    '/app/organization-resource-tags',
    {
      queryParams: {
        organization_slug: organization ?? ''
      }
    }
  );

  const [editOpened, editHandlers] = useDisclosure(false);
  const [deleteOpened, deleteHandlers] = useDisclosure(false);
  const [uploadResourceOpened, uploadResourceHandlers] = useDisclosure(false);
  const [editResourceTagsOpened, editResourceTagsHandlers] = useDisclosure(false);

  const [currentResource, setCurrentResource] = useState<OrganizationResource>();

  const { userHasPermission, isLoading: permissionsLoading } = useOrgPermissions();
  const userCanUploadResource = userHasPermission('upload_resource') && allowResourceUpload;

  const handleOpenEdit = (resource: OrganizationResource) => {
    setCurrentResource(resource);
    editHandlers.open();
  };

  const handleOpenDelete = (resource: OrganizationResource) => {
    setCurrentResource(resource);
    deleteHandlers.open();
  };

  const handleDownload = (resource: OrganizationResource) => {
    setCurrentResource(resource);
    window.open(resource.url ?? '', '_blank', 'noreferrer');
  };

  const {
    resources,
    setPageIndex,
    pagination,
    pageCount,
    isLoading: tableLoading,
    sorting,
    onSortingChange,
    totalNumberOfResources
  } = useResourceListData({
    organizationSlug: organization ?? '',
    searchTags: filters.tags,
    resourceTypes: filters.resourceTypes,
    searchTerm: filters.searchTerm,
    proposalUid: filters.inUse ? proposalUid : ''
  });

  const resetPageIndex = useCallback(() => {
    setPageIndex(0);
  }, [setPageIndex]);
  const isLoading = permissionsLoading || tableLoading || tagsLoading;

  const emptyStateProps: TableEmptyStateProps = (totalNumberOfResources ?? 0) > 0
    ? {
        text: EMPTY_RESOURCES_FILTER_STATE_TEXT
      }
    : {
        text: userCanUploadResource ? ADMIN_EMPTY_RESOURCES_STATE_TEXT : NON_ADMIN_EMPTY_RESOURCES_STATE_TEXT,
        actionButton: userCanUploadResource
          ? {
              label: 'Import',
              onClick: uploadResourceHandlers.open
            }
          : undefined
      };
  const handleSortingChange = (value: string) => {
    onSortingChange([{ id: value, desc: RESOURCE_LABEL_TO_DIRECTION.get(value) === 'DESC' }]);
  };
  const importButton = (
    <Button
      variant='outline'
      leftSection={<FileUploadIcon />}
      onClick={uploadResourceHandlers.open}
    >
      Import Resources
    </Button>
  );
  return (
    <>
      <Modal
        opened={editOpened}
        {...defaultModalProps}
      >
        <EditResource
          resourceUid={currentResource?.uid}
          name={currentResource?.name ?? ''}
          description={currentResource?.description ?? ''}
          resourceType={currentResource?.resource_type as ResourceType}
          onClose={editHandlers.close}
        />
      </Modal>
      <Modal
        opened={deleteOpened}
        {...defaultModalProps}
      >
        <DeleteResource
          resourceUid={currentResource?.uid}
          name={currentResource?.name ?? ''}
          onClose={deleteHandlers.close}
        />
      </Modal>
      <Modal
        opened={editResourceTagsOpened}
        {...defaultModalProps}
      >
        <ManageOrganizationResourceTags
          organizationSlug={organization ?? ''}
          onClose={editResourceTagsHandlers.close}
        />
      </Modal>
      <Modal opened={uploadResourceOpened} {...defaultModalProps}>
        <UploadResources
          onClose={uploadResourceHandlers.close}
          setSelected={resetPageIndex}
          onUploadResources={resetPageIndex}
        />
      </Modal>
      <Group mx={16} justify='space-between'>
        {title}
        {userCanUploadResource && importButton}
      </Group>
      <Group mx={16} justify='space-between'>
        <ResourceFilterGroup
          disabled={tableLoading}
          filters={filters}
          onUpdateFilters={handleUpdateFilters}
          canFilterByInUse={onSelectResource !== undefined}
          availableTags={data?.items ?? []}
          onSortingChange={handleSortingChange}
        />
        <ResourceSortingSelector
          currentSorting={sorting[0].id}
          onSortingChange={handleSortingChange}
          disabled={sorting[0].id === ''}
        />
      </Group>
      <ScrollArea mb={8} flex={1}>
        <Stack mx={16} gap={0}>
          {
            isLoading
              ? <CenteredLoader h={250} />
              : resources.length > 0
                ? resources.map(resource => (
                  <ResourceListItem
                    selected={selectedResourceUids?.includes(resource.uid)}
                    onSelect={onSelectResource}
                    key={resource.uid}
                    organizationResourceTags={data?.items ?? []}
                    resource={resource}
                    onOpenDelete={handleOpenDelete}
                    onDownload={handleDownload}
                    onOpenEdit={handleOpenEdit}
                    onEditResourceTags={editResourceTagsHandlers.open}
                  />
                ))
                : <TableEmptyState {...emptyStateProps} />
          }

        </Stack>
      </ScrollArea>
      <Pagination
        total={pageCount}
        onChange={(value) => { setPageIndex(value - 1); }}
        value={pagination.pageIndex + 1}
      />
    </>
  );
};

export default ResourceList;
